/**
 * Specification search widget for "Finition et Motorisation".
 * Syncs with rangeFiltersState + algoliaRouting (URL param `specification`, configure.filters).
 */

import type { InstantSearch } from "instantsearch.js";
import {
    rangeFiltersState,
    updateSpecificationFilter,
    clearSpecificationFilter as clearSpecificationFilterState,
} from "@/services/algoliaRouting.blade";

let specContainerRef: HTMLElement | null = null;

export function syncSpecificationSearchUi() {
    const spec = rangeFiltersState.value.specification ?? "";
    const inp =
        specContainerRef?.querySelector<HTMLInputElement>("[data-spec-search]");
    if (inp && inp.value !== spec) {
        inp.value = spec;
    }

    const tag = specContainerRef?.querySelector<HTMLElement>(
        ".simple-search-box__tag",
    );
    const tagLabel = specContainerRef?.querySelector<HTMLElement>(
        "[data-spec-tag-label]",
    );
    if (!tag || !tagLabel) return;

    if (spec.trim()) {
        tagLabel.textContent = spec;
        tag.hidden = false;
    } else {
        tag.hidden = true;
    }
}

/** Clears filter + UI. Call BEFORE clearRefinements triggers its search. */
export function clearSpecificationFilter() {
    const inp =
        specContainerRef?.querySelector<HTMLInputElement>("[data-spec-search]");
    if (inp) inp.value = "";
    clearSpecificationFilterState();
    syncSpecificationSearchUi();
}

export function createSpecificationSearchWidget(
    containerSelector: string,
    search: InstantSearch,
) {
    const specContainer =
        document.querySelector<HTMLElement>(containerSelector);
    if (!specContainer) return null;

    specContainerRef = specContainer;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    specContainer.innerHTML = `<div class="simple-search-box">
        <div class="RefinementList__search input__field input__field--facette">
            <input type="search" class="input input--search" data-attr="listing-panel-search" placeholder="TFSI, GTI ..." autocomplete="off" data-spec-search />
        </div>
        <div class="simple-search-box__tag" hidden>
            <div class="current-refinements facet"><div>
                <label class="RefinementList RefinementList--selected" data-attr="listing-panel-clear-facet">
                    <span><span class="RefinementList__labelText" data-spec-tag-label></span></span>
                    <input class="RefinementList__checkbox RefinementList__checkbox--rounded" type="checkbox" checked data-spec-clear />
                </label>
            </div></div>
        </div>
    </div>`;

    const inp =
        specContainer.querySelector<HTMLInputElement>("[data-spec-search]");

    syncSpecificationSearchUi();

    inp?.addEventListener("input", () => {
        const val = inp.value;
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            updateSpecificationFilter(val.trim());
            syncSpecificationSearchUi();
        }, 500);
    });

    specContainer
        .querySelector<HTMLInputElement>("[data-spec-clear]")
        ?.addEventListener("change", () => {
            clearSpecificationFilter();
        });

    const onRender = () => syncSpecificationSearchUi();
    search.on("render", onRender);

    return {
        $$type: "custom.specificationSearch",
        init() {},
        render() {},
        dispose() {
            search.removeListener("render", onRender);
        },
    };
}
